/**
 * RBAC-3.5 — the permission editor (FR-9 to FR-12, UX-DR3, UX-DR5 to UX-DR8, NFR-12).
 *
 * Renders `buildEditor()` and nothing else: what is a ladder, a switch, destructive or locked, and
 * every word on it, comes from `lib/permissionEditor.ts`, which is tested without a DOM.
 *
 * It edits a DRAFT. RBAC-3.6: the banner names the changes and who they affect
 * (`role_holder_count()`, both role systems), and its button opens Review changes; nothing saves
 * without that diff (FR-13). A save drops the draft (`onDraftChange(null)`) and the page refetches.
 *
 * RBAC-3.7: the draft is held by `RolesPermissionsPage`, one per role, so a toggle on the By
 * capability tab and a control here are the same edit to the same draft, and RBAC-3.6 has one thing
 * to save. Mounted with `key={role.id}`, so the remembered scope choices start fresh per role.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Lock } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { ReviewChangesDialog } from '@/components/settings/components/roles/ReviewChangesDialog';
import { UnsavedChangesBanner } from '@/components/settings/roles/UnsavedChangesBanner';
import type { RoleSaveDiff } from '@/lib/rolePermissionDiff';
import { fetchRoleHolders } from '@/services/roleSaveService';
import type { Scope } from '@/lib/fieldPermissions';
import {
  applyLadder,
  applySwitch,
  buildEditor,
  changedItemCount,
  type DestructiveItem,
  type LadderItem,
  type LockedItem,
  type SwitchItem,
} from '@/lib/permissionEditor';

interface Option<T> {
  value: T;
  label: string;
}

/** An ordinal control. Buttons that wrap, so 13 areas never scroll sideways (NFR-12). */
function Segmented<T extends string | null>({
  label,
  options,
  value,
  disabled,
  onChange,
}: {
  label: string;
  options: Option<T>[];
  value: T;
  disabled: boolean;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
      <span className="w-36 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
        {options.map((o) => {
          const checked = o.value === value;
          return (
            <button
              key={String(o.value)}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => onChange(o.value)}
              className={cn(
                'rounded-md border px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed',
                checked
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:bg-muted',
                disabled && !checked && 'opacity-60',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LadderRow({
  item,
  readOnly,
  onChange,
}: {
  item: LadderItem;
  readOnly: boolean;
  onChange: (capability: string | null, scope: Scope | null) => void;
}) {
  const scope = item.scope?.value ?? null;
  return (
    <div className="space-y-2.5 py-4">
      <div>
        <div className="text-sm font-medium text-foreground">{item.title}</div>
        <p className="text-sm text-muted-foreground">{item.description}</p>
      </div>
      <Segmented
        label="What they can do"
        options={item.rungs}
        value={item.capability}
        disabled={readOnly}
        onChange={(c) => onChange(c, scope)}
      />
      {/* FR-9a: no scope control at all where the noun has no enforced scope. */}
      {item.scope && (
        <Segmented
          label="Whose"
          options={item.scope.options}
          value={item.scope.value}
          disabled={readOnly}
          onChange={(s) => onChange(item.capability, s)}
        />
      )}
      <p className="rounded-md bg-muted/60 px-3 py-2 text-sm text-foreground" aria-live="polite">
        {item.consequence}
      </p>
    </div>
  );
}

function SwitchRow({
  item,
  readOnly,
  onChange,
}: {
  item: SwitchItem;
  readOnly: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = `perm-${item.keys[0]}`;
  return (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-foreground">{item.label}</label>
        <p className="text-sm text-muted-foreground">{item.description}</p>
      </div>
      <Switch id={id} checked={item.on} disabled={readOnly} onCheckedChange={onChange} className="mt-0.5 shrink-0" />
    </div>
  );
}

/** UX-DR7: outside every ladder, in the destructive colour, with its own paragraph. */
function DestructiveRow({
  item,
  readOnly,
  onChange,
}: {
  item: DestructiveItem;
  readOnly: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = `perm-${item.keys[0]}`;
  return (
    <div className="my-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {item.label}
          </label>
          <p className="mt-0.5 text-sm text-foreground">{item.description}</p>
          <p className="mt-1.5 text-sm text-destructive">{item.irreversible}</p>
        </div>
        <Switch
          id={id}
          checked={item.on}
          disabled={readOnly}
          onCheckedChange={onChange}
          className="mt-0.5 shrink-0 data-[state=checked]:bg-destructive"
        />
      </div>
    </div>
  );
}

/** UX-DR8: a padlock and an explanation, never a bare disabled control. */
function LockedRow({ item }: { item: LockedItem }) {
  return (
    <div className="flex items-start gap-3 py-4">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{item.label}</div>
        <p className="text-sm text-muted-foreground">{item.description}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {item.held ? 'This role has it.' : 'This role does not have it.'}
        </p>
      </div>
    </div>
  );
}

export function PermissionEditor({
  roleId,
  permissions,
  draft,
  onDraftChange,
  readOnly,
  onSaved,
}: {
  roleId: number;
  /** As saved. */
  permissions: string[];
  /** The role's draft, which is `permissions` when nothing has been changed. */
  draft: readonly string[];
  /** `null` discards the draft. */
  onDraftChange: (next: string[] | null) => void;
  readOnly: boolean;
  /** RBAC-3.6: after the save lands. The draft has already been dropped. */
  onSaved?: (result: RoleSaveDiff) => void;
}) {
  const [reviewing, setReviewing] = useState(false);
  const [scopeChoice, setScopeChoice] = useState<Record<string, Scope>>({});
  const setDraft = (f: (d: readonly string[]) => string[]) => onDraftChange(f(draft));
  const areas = useMemo(() => buildEditor(draft, scopeChoice), [draft, scopeChoice]);
  const changes = useMemo(() => changedItemCount(permissions, draft), [permissions, draft]);

  // The banner's count. Only an account's own role can be saved, so only it is counted.
  const holders = useQuery({
    queryKey: ['rbac', 'role-holders', roleId],
    queryFn: () => fetchRoleHolders(roleId),
    enabled: !readOnly && changes > 0,
    staleTime: 30 * 1000,
  });

  const setLadder = (noun: string, capability: string | null, scope: Scope | null) => {
    if (scope) setScopeChoice((c) => ({ ...c, [noun]: scope }));
    setDraft((d) => applyLadder(d, noun, capability, scope));
  };
  const setSwitch = (keys: string[], on: boolean) => setDraft((d) => applySwitch(d, keys, on));

  return (
    <div className="space-y-3">
      {!readOnly && changes > 0 && (
        <UnsavedChangesBanner
          changes={changes}
          holders={holders.data ?? null}
          holdersState={holders.isError ? 'error' : holders.data ? 'ready' : 'loading'}
          onDiscard={() => {
            onDraftChange(null);
            setScopeChoice({});
          }}
          onSave={() => setReviewing(true)}
        />
      )}
      {!readOnly && (
        <ReviewChangesDialog
          open={reviewing}
          roleId={roleId}
          base={permissions}
          draft={[...draft]}
          onOpenChange={setReviewing}
          onSaved={(r) => {
            onDraftChange(null);
            setScopeChoice({});
            void holders.refetch();
            onSaved?.(r);
          }}
        />
      )}

      <Accordion type="single" collapsible className="rounded-md border border-border">
        {areas.map((a) => (
          <AccordionItem key={a.area} value={a.area} className="border-border last:border-b-0">
            <AccordionTrigger className="px-4 py-3 text-sm hover:no-underline">
              <span className="flex min-w-0 flex-1 items-center justify-between gap-3 pr-3">
                <span className="truncate font-medium text-foreground">{a.area}</span>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-normal tabular-nums text-muted-foreground">
                  {a.summary}
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="divide-y divide-border px-4 pb-1">
              {a.items.map((i) => {
                switch (i.kind) {
                  case 'ladder':
                    return (
                      <LadderRow key={i.noun} item={i} readOnly={readOnly} onChange={(c, s) => setLadder(i.noun, c, s)} />
                    );
                  case 'switch':
                    return (
                      <SwitchRow key={i.keys.join()} item={i} readOnly={readOnly} onChange={(on) => setSwitch(i.keys, on)} />
                    );
                  case 'destructive':
                    return (
                      <DestructiveRow key={i.keys.join()} item={i} readOnly={readOnly} onChange={(on) => setSwitch(i.keys, on)} />
                    );
                  case 'locked':
                    return <LockedRow key={i.keys.join()} item={i} />;
                }
              })}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
